import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { useAnalysis } from "@/context/AnalysisContext";
import { sampleVAInputs } from "@/lib/calculations";
import { track } from "@/lib/analytics";
import { Wallet, Coins, ClipboardCheck, ShieldCheck, ArrowRight, RotateCcw, Sparkles, X } from "lucide-react";

const NUDGE_DISMISSED_KEY = "ctk_sample_nudge_v1";

const benefits = [
  { icon: Wallet, title: "True Monthly Cost", body: "Principal, interest, taxes, insurance, HOA, and other recurring housing expenses in one clear number." },
  { icon: Coins, title: "Cash Before Closing", body: "Plan for earnest money, inspections, appraisal, and expenses that may arise during the transaction." },
  { icon: ClipboardCheck, title: "Cash at Closing", body: "See how closing costs, seller credits, lender credits, down payment, and earnest-money credits interact." },
  { icon: ShieldCheck, title: "VA-Specific Guidance", body: "Estimate funding fees, zero-down scenarios, credits, and VA-specific purchase considerations." },
];

export default function Landing() {
  const { hasWorksheet, reset, setInputs } = useAnalysis();
  const nav = useNavigate();
  const startOver = () => {
    if (!window.confirm("Start a new analysis? Your current unsaved changes will be cleared.")) return;
    reset();
  };

  // First-visit nudge: only shows if the user has not previously dismissed it
  // AND does not already have worksheet data. Never blocks; the modal has a
  // close button plus "Start my own analysis" that dismisses without loading
  // the sample.
  const [showNudge, setShowNudge] = useState(false);
  useEffect(() => {
    track("landing_view");
    try {
      const dismissed = localStorage.getItem(NUDGE_DISMISSED_KEY);
      if (!dismissed && !hasWorksheet) setShowNudge(true);
    } catch { /* ignore storage failures */ }
  }, [hasWorksheet]);

  const dismissNudge = () => {
    try { localStorage.setItem(NUDGE_DISMISSED_KEY, "1"); } catch { /* noop */ }
    setShowNudge(false);
  };

  const loadSample = () => {
    setInputs(sampleVAInputs());
    dismissNudge();
    nav("/results");
  };

  return (
    <>
      <Nav />
      {showNudge && (
        <div data-testid="sample-nudge-overlay" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0 bg-brand-ink/40 backdrop-blur-sm animate-fade-up">
          <div className="relative w-full max-w-md card-hs p-6 sm:p-7 shadow-lg">
            <button data-testid="sample-nudge-close" onClick={dismissNudge}
              aria-label="Close" className="absolute top-3 right-3 text-brand-muted hover:text-brand-ink">
              <X size={18} />
            </button>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-brand-accent bg-brand-soft rounded-full px-2 py-0.5">
              <Sparkles size={12} /> New here?
            </span>
            <h2 className="font-display text-2xl text-brand-ink mt-3 leading-tight">
              Want to see how Home Cost Path works? Try a sample VA home.
            </h2>
            <p className="text-sm text-brand-muted mt-2 leading-relaxed">
              We'll load a realistic zero-down VA scenario at $385,000 so you can explore the full analysis instantly. You can edit any number afterward.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-2">
              <button data-testid="sample-nudge-try" onClick={loadSample} className="btn-primary flex-1">
                <Sparkles size={16} /> Try a sample VA home
              </button>
              <button data-testid="sample-nudge-own" onClick={() => { dismissNudge(); nav("/analyze"); }} className="btn-secondary flex-1">
                Start my own
              </button>
            </div>
            <p className="text-[11px] text-brand-muted mt-3 text-center">You can start your own analysis anytime — this is just an optional preview.</p>
          </div>
        </div>
      )}
      <main>
        {hasWorksheet && (
          <div data-testid="continue-banner" className="border-b border-brand-border bg-brand-soft/70">
            <div className="container-hs py-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-brand-ink">You have a Home Cost Path analysis in progress on this device.</div>
              <div className="flex gap-2">
                <button data-testid="continue-analysis" onClick={() => nav("/analyze")} className="btn-primary py-2 px-4 text-sm">Continue your Home Cost Path analysis <ArrowRight size={14}/></button>
                <button data-testid="start-over" onClick={startOver} className="btn-secondary py-2 px-4 text-sm"><RotateCcw size={14}/> Start over</button>
              </div>
            </div>
          </div>
        )}
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="container-hs pt-16 pb-20 md:pt-24 md:pb-28 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7 animate-fade-up">
              <span className="badge-soft" data-testid="hero-tag">See the real cost before you buy</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-ink mt-5 leading-[1.05]">
                Know what this <span className="text-brand-accent">house</span> will really cost you.
              </h1>
              <p className="mt-6 text-base sm:text-lg text-brand-muted max-w-2xl leading-relaxed">
                Estimate your monthly payment, closing costs, earnest money, inspections, appraisal, seller credits,
                and the cash you may need — before you ever receive the keys.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/analyze" data-testid="cta-analyze" className="btn-primary">
                  Analyze a Home <ArrowRight size={18} />
                </Link>
                <Link to="/compare" data-testid="cta-compare" className="btn-secondary">Compare Homes</Link>
              </div>
              <p className="mt-6 text-xs text-brand-muted max-w-xl">
                Home Cost Path is an independent educational planning tool and is not affiliated with or endorsed by the
                U.S. Department of Veterans Affairs.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-brand-border shadow-md">
                <img
                  alt="Bright, modern living room with warm daylight"
                  src="/img/landing-hero.jpg"
                  loading="lazy"
                  className="w-full h-[420px] object-cover"
                />
                <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/95 backdrop-blur px-5 py-4 shadow">
                  <div className="text-xs uppercase tracking-wider text-brand-muted">Sample Estimate</div>
                  <div className="flex items-end justify-between mt-1 gap-3">
                    <div>
                      <div className="num-lg">$2,847<span className="text-lg text-brand-muted font-medium">/mo</span></div>
                      <div className="text-xs text-brand-muted">Estimated true monthly housing cost</div>
                    </div>
                    <div className="text-right">
                      <div className="text-brand-accent font-semibold text-2xl leading-none">$7,800</div>
                      <div className="text-xs text-brand-muted mt-1">Est. max cash needed<br/>during purchase</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="container-hs py-8">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((b, i) => (
              <div key={b.title} data-testid={`benefit-${i}`} className="card-hs p-6 transition-transform hover:-translate-y-1">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-soft text-brand-accent"><b.icon size={20} /></span>
                <h3 className="mt-4 font-display text-lg text-brand-ink">{b.title}</h3>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Explainer */}
        <section className="container-hs py-20">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-6">
              <h2 className="font-display text-3xl sm:text-4xl text-brand-ink tracking-tight">Not just a mortgage calculator.</h2>
              <p className="mt-4 text-brand-muted leading-relaxed">
                Most calculators answer "what's my monthly payment?" Home Cost Path answers a bigger question:
                <em className="text-brand-ink not-italic"> how much cash do I actually need to buy this house</em>,
                and can I comfortably afford it? We surface money you may need before closing, at closing, and the
                maximum amount you may need access to during the transaction.
              </p>
              <ul className="mt-6 space-y-3 text-sm">
                {["Earnest money is never double-counted", "Seller & lender credits reduce cash at closing", "VA funding-fee logic is fully configurable", "Plain-English summary you can share"].map(x => (
                  <li key={x} className="flex gap-3"><span className="mt-1 h-1.5 w-1.5 rounded-full bg-brand-accent" />{x}</li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-6">
              <div className="rounded-3xl border border-brand-border p-6 bg-white shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { k: "Cash before closing", v: "$1,175", sub: "Inspection + appraisal" },
                    { k: "Cash at closing", v: "$4,320", sub: "After seller credit" },
                    { k: "Max transaction cash", v: "$5,495", sub: "Peak requirement" },
                    { k: "Cash remaining", v: "$2,505", sub: "Estimated cushion" },
                  ].map((s) => (
                    <div key={s.k} className="rounded-2xl bg-brand-bg p-5">
                      <div className="text-xs text-brand-muted">{s.k}</div>
                      <div className="text-2xl font-semibold mt-1 text-brand-ink">{s.v}</div>
                      <div className="text-xs text-brand-muted mt-1">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
